import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { generateLinkCode, claimDevice } from '../lib/api';
import { setDeviceId, setNickname, setLinkCode, getLinkCode } from '../lib/db';

interface DeviceLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DeviceLinkModal({ isOpen, onClose }: DeviceLinkModalProps) {
  const [myCode, setMyCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load existing code on mount
  useEffect(() => {
    if (isOpen) {
      getLinkCode().then((code) => {
        if (code) setMyCode(code);
      });
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const { code } = await generateLinkCode();
      setMyCode(code);
      await setLinkCode(code);
    } catch {
      setError('Failed to generate link code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!claimCode.trim()) return;
    setIsClaiming(true);
    setError(null);
    setSuccess(null);
    try {
      const { deviceId, nickname } = await claimDevice(claimCode.trim());
      await setDeviceId(deviceId);
      if (nickname) {
        await setNickname(nickname);
      }
      setSuccess(
        'Your identity has been loaded. Your session cards from the other device are now accessible here. Please reload the page.'
      );
      setClaimCode('');
    } catch {
      setError('Invalid link code. Please check and try again.');
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Use on Another Device"
      footer={
        <button onClick={onClose} className="btn btn-secondary">
          Close
        </button>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
        )}
        {success && (
          <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">{success}</div>
        )}

        {/* Generate / show personal code */}
        <div>
          <h3 className="font-medium text-gray-900 mb-1">Your Personal Code</h3>
          <p className="text-sm text-gray-500 mb-3">
            Generate a personal code here, then enter it on your other device to load your session
            cards and identity.
          </p>
          {myCode ? (
            <div className="flex items-center gap-3">
              <span className="text-2xl font-mono font-bold tracking-widest text-gray-900 bg-gray-100 px-4 py-2 rounded-lg select-all">
                {myCode}
              </span>
              <span className="text-sm text-gray-500">Share this code with your other device</span>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="btn btn-primary"
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate My Code'}
            </button>
          )}
        </div>

        <div className="border-t border-gray-200" />

        {/* Claim identity from another device */}
        <div>
          <h3 className="font-medium text-gray-900 mb-1">Load From Another Device</h3>
          <p className="text-sm text-gray-500 mb-3">
            Enter the personal code from your other device to load your session cards and identity
            here.
          </p>
          <form onSubmit={handleClaim} className="flex gap-2">
            <input
              type="text"
              className="input flex-1 font-mono uppercase tracking-widest"
              placeholder="Enter code"
              value={claimCode}
              onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
              maxLength={8}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isClaiming || !claimCode.trim()}
            >
              {isClaiming ? 'Loading...' : 'Load'}
            </button>
          </form>
        </div>
      </div>
    </Modal>
  );
}
